/**
 * Shared type definitions for IOMI.
 * Used by web, mobile, and backend.
 * Framework-agnostic — no React, no DOM, no React Native imports.
 */

// ── Generic ─────────────────────────────────────────────────────────────────

export interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
}

export type LoadingState = 'idle' | 'loading' | 'success' | 'error';

// ── Users ───────────────────────────────────────────────────────────────────

export interface AppUser {
  uid: string;
  email: string;
  displayName: string;
  isPlatformAdmin: boolean;
}

export interface CurrentUser {
  uid: string;
  email: string;
  displayName: string;
  isPlatformAdmin: boolean;
  photoBase64?: string | null;
  phone?: string | null;
  providerIds: string[];
  onboardingCompletedAt?: string | null;
  onboardingStep?: number | null;
}

// ── Organizations ───────────────────────────────────────────────────────────

export type OrgCurrency = 'USD' | 'EUR' | 'GBP';
export type OrgUnits = 'imperial' | 'metric';
export type TravelTimeRate = 'one_hour_standard' | 'portal_to_portal';

export interface OrgStripeData {
  customerId: string;
  subscriptionId: string;
  status: string;
  subscriptionQuantity: number;
  priceId: string;
  productId?: string;
  cancelAtPeriodEnd: boolean;
}

export interface OrgSubscriptionCost {
  unitAmount: number;
  totalAmount: number;
  currency: string;
  interval: string;
}

// ── Stripe Connect ─────────────────────────────────────────────────────────

export type StripeConnectStatus =
  | 'not_started'
  | 'onboarding'
  | 'pending_verification'
  | 'active'
  | 'restricted'
  | 'disabled';

export interface OrgStripeConnectData {
  accountId: string;
  status: StripeConnectStatus;
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  detailsSubmitted: boolean;
  onboardingCompletedAt?: string;
  createdAt: string;
}

// ── Payment Schedule ───────────────────────────────────────────────────────

export type PaymentScheduleType =
  | 'deposit_only'
  | 'deposit_and_final'
  | 'deposit_split_final'
  | 'full_at_completion'
  | 'custom';

export type PaymentInstallmentStatus = 'pending' | 'due' | 'paid' | 'overdue' | 'waived';

export interface PaymentInstallment {
  id: string;
  label: string;
  type: 'deposit' | 'installment' | 'final';
  amountCents: number;
  percentage?: number;
  dueDate?: string;
  dueTrigger?: 'on_booking' | 'before_move' | 'on_completion' | 'manual';
  status: PaymentInstallmentStatus;
  paymentId?: string;
}

export interface PaymentSchedule {
  type: PaymentScheduleType;
  installments: PaymentInstallment[];
}

/** Org-level default payment schedule preferences (configured in Customer Payments admin). */
export interface PaymentScheduleDefaults {
  /** Whether the org collects a deposit to lock in the move date. */
  collectDeposit: boolean;
  /** How the deposit amount is determined. */
  depositType: 'fixed' | 'percent';
  /** Deposit amount — dollar amount if 'fixed', percentage (0–100) if 'percent'. */
  depositAmount?: number;
  /** How to collect the remaining balance after the deposit.
   *  Only relevant when collectDeposit is true AND deposit is less than 100%.
   *  - 'before_move' — single payment before the move starts
   *  - 'on_completion' — single payment after the move ends
   *  - 'split' — split between before-move and on-completion
   */
  balanceCollection?: 'before_move' | 'on_completion' | 'split';
  /** Percentage of the remaining balance due before the move (only when balanceCollection === 'split'). */
  splitBeforeMovePercent?: number;
}

// ── Payment Records ────────────────────────────────────────────────────────

export type PaymentStatus =
  | 'pending'
  | 'processing'
  | 'succeeded'
  | 'failed'
  | 'canceled';

export interface Payment {
  id: string;
  orgId: string;
  quoteId: string;
  installmentId: string;
  stripePaymentIntentId: string;
  amountCents: number;
  currency: string;
  platformFeeCents: number;
  status: PaymentStatus;
  customerEmail?: string;
  customerName?: string;
  failureReason?: string;
  createdAt: string;
  updatedAt: string;
  paidAt?: string;
  /** Payment source. Defaults to 'stripe' for backwards compatibility. */
  source?: 'stripe' | 'manual';
}

// ── Offline Payment Methods ───────────────────────────────────────────────

export type OfflinePaymentMethodType =
  | 'venmo'
  | 'cashapp'
  | 'paypal'
  | 'zelle'
  | 'check'
  | 'cash'
  | 'custom';

/** Per-method configuration stored on the org. */
export interface OrgPaymentMethodConfig {
  type: OfflinePaymentMethodType;
  enabled: boolean;
  /** Display label (auto-set for known types, user-defined for 'custom'). */
  label: string;
  /** Method-specific identifier (Venmo handle, $cashtag, PayPal.me handle, Zelle phone/email, check payable name, or custom instructions). */
  identifier?: string;
  /** Whether this is the org's preferred payment method. Only one method should be preferred at a time. */
  preferred?: boolean;
}

/** Top-level payment configuration on the org. */
export interface OrgPaymentConfig {
  methods: OrgPaymentMethodConfig[];
  /** Whether to include Stripe payment link when Stripe Connect is enabled. Defaults to true. */
  includeStripeInRequests?: boolean;
  /** Custom message appended to payment request SMS/emails. */
  customMessage?: string;
}

/** Record of a non-Stripe payment logged by a mover. */
export interface ManualPayment {
  id: string;
  orgId: string;
  quoteId: string;
  /** The offline method used. */
  method: OfflinePaymentMethodType;
  amountCents: number;
  currency: string;
  /** Optional reference number (check number, transaction ID, etc). */
  referenceNumber?: string;
  /** Optional note from the mover. */
  note?: string;
  /** Firebase Storage URL for check photo. */
  checkPhotoUrl?: string;
  /** UID of the mover who logged this payment. */
  loggedBy: string;
  createdAt: string;
}

/** Record of a payment request sent to a customer. */
export interface PaymentRequestRecord {
  id: string;
  orgId: string;
  quoteId: string;
  /** Balance due at time of request, in cents. */
  amountCents: number;
  /** Channels used to deliver the request. */
  channels: ('sms' | 'email')[];
  /** Payment methods included in the message. */
  methodsIncluded: (OfflinePaymentMethodType | 'stripe')[];
  /** Stripe Checkout/Payment Link URL if included. */
  stripePaymentUrl?: string;
  sentBy: string;
  sentAt: string;
}

// ── Platform Fee ───────────────────────────────────────────────────────────

export interface PlatformFeeConfig {
  type: 'percentage' | 'flat' | 'percentage_plus_flat';
  percentage?: number;
  flatAmountCents?: number;
}

// ── Job Payment Summary ────────────────────────────────────────────────────

export type JobPaymentStatus = 'no_schedule' | 'unpaid' | 'partially_paid' | 'fully_paid' | 'overpaid';

export interface JobPaymentSummary {
  paymentStatus: JobPaymentStatus;
  /** Pre-tax total in cents. Old documents without this field treat totalAmountCents as both. */
  subtotalAmountCents?: number;
  totalAmountCents: number;
  collectedAmountCents: number;
  outstandingAmountCents: number;
  paymentScheduleType?: PaymentScheduleType;
  nextPaymentDue?: {
    installmentId: string;
    label: string;
    amountCents: number;
    dueDate?: string;
  };
  // ── Tax fields ──
  /** 2-letter state code that governs the tax rate. */
  taxState?: string;
  /** Tax rate for labor/moving services. */
  laborTaxRate?: number;
  /** Tax rate applied to materials; 0 when the governing state does not tax them. */
  materialsTaxRate?: number;
  /** Tax amount in cents. */
  taxAmountCents?: number;
  /** True when interstate + governingStateRule = 'manual_review'. */
  requiresManualTaxReview?: boolean;
  /** True when the org's defaultTaxRate was used instead of a zip.tax lookup. Backend/audit only. */
  usedDefaultTaxRate?: boolean;
}

export type QuoteNumberSeparator = '-' | '.' | ' ' | '';
export type QuoteNumberDigitCount = 3 | 4 | 5 | 6;
export type QuoteNumberMethod = 'sequential' | 'year-sequential' | 'random' | 'date-based';

export interface QuoteNumberConfig {
  prefix: string;
  separator: QuoteNumberSeparator;
  digitCount: QuoteNumberDigitCount;
  startingNumber: number;
  method: QuoteNumberMethod;
}

export const QUOTE_NUMBER_CONFIG_DEFAULTS: QuoteNumberConfig = {
  prefix: 'QT',
  separator: '-',
  digitCount: 5,
  startingNumber: 1,
  method: 'sequential',
};

/** State mover license entry (state abbreviation + license number). */
export interface StateLicense {
  state: string;
  licenseNumber: string;
}

export interface BookingConfig {
  enabled: boolean;
  welcomeMessage?: string;
  enableLockInDate: boolean;
  enableRequestEstimate: boolean;
  /** Whether to show a price estimate to the customer on the booking form. Defaults to true. */
  enableShowPriceEstimate?: boolean;
  depositType?: 'fixed' | 'percent';
  depositAmount?: number;
  customDiscoverySources?: string[];
  /** Whether online quotes for customers are enabled (shareable link). */
  enableOnlineQuote?: boolean;
  /** Whether to accept payments via Stripe Connect for online quotes. Defaults to true when enableOnlineQuote is true. */
  enableAcceptPayments?: boolean;
  /** Primary brand color hex code for customer-facing pages. */
  brandPrimaryColor?: string;
  /** Title text color hex code — auto-selected for contrast, user-overridable. */
  brandTitleTextColor?: string;
}

export interface Organization {
  id: string;
  name: string;
  /** URL-safe slug used in the public booking URL (booking.iomipro.com/{slug}). Unique across all orgs. */
  slug?: string;
  logoBase64?: string;
  maxUsers: number;
  emailDomain?: string;
  inviteEmail?: string;
  defaultCurrency?: OrgCurrency;
  defaultUnits?: OrgUnits;
  efficiencyMultiplier?: number;
  defaultTravelTimeRate?: TravelTimeRate;
  /** Fixed travel time hours when travel rate is 'one_hour_standard'. Defaults to 1. */
  fixedTravelTimeHours?: number;
  // ── Commercial-specific quote defaults ──
  /** Commercial default travel time rate. Overrides generic defaultTravelTimeRate for commercial quotes. */
  commercialDefaultTravelTimeRate?: TravelTimeRate;
  /** Commercial fixed travel time hours when rate is 'one_hour_standard'. Defaults to 1. */
  commercialFixedTravelTimeHours?: number;
  /** Commercial efficiency multiplier. Overrides generic efficiencyMultiplier for commercial quotes. */
  commercialEfficiencyMultiplier?: number;
  /** Commercial default quote validity days. Overrides generic defaultQuoteValidityDays for commercial quotes. */
  commercialDefaultQuoteValidityDays?: number | null;
  // ── Residential-specific quote defaults ──
  /** Residential default travel time rate. */
  residentialDefaultTravelTimeRate?: TravelTimeRate;
  /** Residential fixed travel time hours when rate is 'one_hour_standard'. Defaults to 1. */
  residentialFixedTravelTimeHours?: number;
  /** Residential efficiency multiplier. */
  residentialEfficiencyMultiplier?: number;
  /** Residential default quote validity days. */
  residentialDefaultQuoteValidityDays?: number | null;
  /** Residential pricing model: flat_rate or hourly_rate. Defaults to 'hourly_rate'. */
  residentialPricingModel?: 'flat_rate' | 'hourly_rate';
  /** Bill one blended "Truck + Driver" line instead of separate Driver and Truck lines. */
  useBlendedTruckDriverRate?: boolean;
  billingEmail?: string;
  /** Legal company name shown on client-facing quotes (if different from org name). */
  legalName?: string;
  /** Physical business address. */
  businessAddress?: { address: string; placeId: string };
  /** Company phone number. */
  phone?: string;
  /** Company website URL. */
  website?: string;
  /** USDOT number (required for interstate moves in the U.S.). */
  usdotNumber?: string;
  /** MC number (Motor Carrier authority for interstate). */
  mcNumber?: string;
  /** State mover license numbers (multiple allowed, one per state). */
  stateLicenses?: StateLicense[];
  /** Footer text for generated quote PDFs. */
  quoteFooter?: string;
  quoteNumberConfig?: QuoteNumberConfig;
  nextQuoteNumber?: number;
  nextQuoteNumberByYear?: Record<string, number>;
  /** Default number of days a quote version stays valid. null = never expires. */
  defaultQuoteValidityDays?: number | null;
  /** Feature flags — all default to true (enabled) when undefined. */
  featureDispatch?: boolean;
  featureJobs?: boolean;
  featureClientPortal?: boolean;
  featureQuotePdfs?: boolean;
  /** Whether SmartMoving integration is enabled for this org. */
  featureSmartMovingIntegration?: boolean;
  /** Whether commercial moving quotes are enabled. Defaults to true for existing orgs. */
  featureCommercialMovingQuotes?: boolean;
  /** Whether residential moving quotes are enabled. Defaults to false for existing orgs. */
  featureResidentialMovingQuotes?: boolean;
  /** Whether the Lead Pipeline feature is enabled. Defaults to false. */
  featureLeadPipeline?: boolean;
  /** Whether Fleet Management is enabled. Defaults to false. */
  featureFleetManagement?: boolean;
  /** Whether Online Lead Capture (booking form) is enabled. Defaults to false. Requires featureLeadPipeline. */
  featureOnlineLeadCapture?: boolean;
  /** Whether Stripe Connect payment processing is enabled. Defaults to false. */
  featureStripeConnect?: boolean;
  /** Allowed features — controlled by IOMI admins. Determines which features an org is permitted to use. */
  allowedFeatureDispatch?: boolean;
  allowedFeatureJobs?: boolean;
  allowedFeatureClientPortal?: boolean;
  allowedFeatureQuotePdfs?: boolean;
  allowedFeatureSmartMovingIntegration?: boolean;
  allowedFeatureCommercialMovingQuotes?: boolean;
  allowedFeatureResidentialMovingQuotes?: boolean;
  allowedFeatureLeadPipeline?: boolean;
  allowedFeatureFleetManagement?: boolean;
  allowedFeatureOnlineLeadCapture?: boolean;
  allowedFeatureStripeConnect?: boolean;
  /** Booking form configuration — controls what customers see on the public booking page. */
  bookingConfig?: BookingConfig;
  stripe?: OrgStripeData;
  stripeConnect?: OrgStripeConnectData;
  /** Offline payment method configuration. */
  paymentConfig?: OrgPaymentConfig;
  /** Whether automatic tax calculation is enabled for this org. Defaults to false. */
  calculateTaxes?: boolean;
  /** Default tax rate used when automatic tax calculation is disabled (e.g. 0.05 for 5%). */
  defaultTaxRate?: number;
  /** Default payment schedule preferences. */
  paymentScheduleDefaults?: PaymentScheduleDefaults;
  subscriptionCost?: OrgSubscriptionCost;
  stripeStatus?: string;
  createdAt: string;
  updatedAt: string;
}

// ── Org Setup Checklist ────────────────────────────────────────────────────

/** Tracks which sections of the org setup wizard have been completed. */
export interface OrgSetupChecklist {
  companyProfileCompleted: boolean;
  moveTypesCompleted: boolean;
  fleetCompleted: boolean;
  residentialSettingsCompleted: boolean;
  commercialSettingsCompleted: boolean;
  paymentsCompleted: boolean;
  /** Set when the admin reviews material prices (a confirmation, like payments: blank costs are allowed). */
  materialsPricingCompleted: boolean;
  /** Set when the wizard is fully walked through (even if some sections were skipped). */
  completedAt: string | null;
  /** Set when the admin dismisses the setup banner. Re-shows after 7 days. */
  skippedAt: string | null;
}

/** Setup checklist with computed applicability based on org feature flags. */
export interface OrgSetupChecklistStatus extends OrgSetupChecklist {
  /** Which sections are applicable for this org based on its feature flags. */
  applicableSections: (keyof Pick<OrgSetupChecklist,
    'companyProfileCompleted' | 'moveTypesCompleted' | 'fleetCompleted' |
    'residentialSettingsCompleted' | 'commercialSettingsCompleted' | 'paymentsCompleted' |
    'materialsPricingCompleted'
  >)[];
  /** Whether all applicable sections are complete. */
  isComplete: boolean;
}

// ── Org defaults resolution helpers ─────────────────────────────────────────
// These resolve the correct per-move-type setting, falling back to the generic
// org-wide value for backward compatibility with orgs that predate the split.

/** Resolve the org-level default travel time rate for a given quote type. */
export function resolveOrgTravelTimeRate(
  org: Pick<Organization, 'commercialDefaultTravelTimeRate' | 'residentialDefaultTravelTimeRate' | 'defaultTravelTimeRate'> | null | undefined,
  quoteType: 'commercial' | 'residential' | undefined,
): TravelTimeRate {
  if (!org) return 'one_hour_standard';
  if (quoteType === 'residential') {
    return org.residentialDefaultTravelTimeRate ?? org.defaultTravelTimeRate ?? 'one_hour_standard';
  }
  return org.commercialDefaultTravelTimeRate ?? org.defaultTravelTimeRate ?? 'one_hour_standard';
}

/** Resolve the org-level default efficiency multiplier for a given quote type. */
export function resolveOrgEfficiencyMultiplier(
  org: Pick<Organization, 'commercialEfficiencyMultiplier' | 'residentialEfficiencyMultiplier' | 'efficiencyMultiplier'> | null | undefined,
  quoteType: 'commercial' | 'residential' | undefined,
): number {
  if (!org) return 1;
  if (quoteType === 'residential') {
    return org.residentialEfficiencyMultiplier ?? org.efficiencyMultiplier ?? 1;
  }
  return org.commercialEfficiencyMultiplier ?? org.efficiencyMultiplier ?? 1;
}

/** Resolve the org-level fixed travel time hours for a given quote type. */
export function resolveOrgFixedTravelTimeHours(
  org: Pick<Organization, 'commercialFixedTravelTimeHours' | 'residentialFixedTravelTimeHours' | 'fixedTravelTimeHours'> | null | undefined,
  quoteType: 'commercial' | 'residential' | undefined,
): number {
  if (!org) return 1;
  if (quoteType === 'residential') {
    return org.residentialFixedTravelTimeHours ?? org.fixedTravelTimeHours ?? 1;
  }
  return org.commercialFixedTravelTimeHours ?? org.fixedTravelTimeHours ?? 1;
}

/** Resolve the org-level default quote validity days for a given quote type. */
export function resolveOrgValidityDays(
  org: Pick<Organization, 'commercialDefaultQuoteValidityDays' | 'residentialDefaultQuoteValidityDays' | 'defaultQuoteValidityDays'> | null | undefined,
  quoteType: 'commercial' | 'residential' | undefined,
): number | null | undefined {
  if (!org) return undefined;
  if (quoteType === 'residential') {
    return org.residentialDefaultQuoteValidityDays !== undefined
      ? org.residentialDefaultQuoteValidityDays
      : org.defaultQuoteValidityDays;
  }
  return org.commercialDefaultQuoteValidityDays !== undefined
    ? org.commercialDefaultQuoteValidityDays
    : org.defaultQuoteValidityDays;
}

export type OrgRole = 'admin' | 'estimator' | 'mover' | 'dispatcher' | 'field_lead';

export interface OrgUser {
  uid: string;
  email: string;
  displayName: string;
  role: OrgRole;
  status?: 'active' | 'invited' | 'inactive';
  photoBase64?: string | null;
}

export interface OrgMembership {
  orgId: string;
  orgName: string;
  role: OrgRole;
  /** Feature flags — all default to true (enabled) when undefined. */
  featureDispatch?: boolean;
  featureJobs?: boolean;
  featureClientPortal?: boolean;
  featureQuotePdfs?: boolean;
  /** Whether SmartMoving integration is enabled for this org. */
  featureSmartMovingIntegration?: boolean;
  /** Whether commercial moving quotes are enabled. Defaults to true. */
  featureCommercialMovingQuotes?: boolean;
  /** Whether residential moving quotes are enabled. Defaults to false. */
  featureResidentialMovingQuotes?: boolean;
  /** Whether the Lead Pipeline feature is enabled. Defaults to false. */
  featureLeadPipeline?: boolean;
  /** Whether Fleet Management is enabled. Defaults to false. */
  featureFleetManagement?: boolean;
  /** Whether Online Lead Capture (booking form) is enabled. Defaults to false. */
  featureOnlineLeadCapture?: boolean;
  /** Whether Stripe Connect payment processing is enabled. Defaults to false. */
  featureStripeConnect?: boolean;
  /** Whether Customer Payments is enabled for this org. Defaults to false. */
  featureCustomerPayments?: boolean;
}

// ── Resources & Crew ────────────────────────────────────────────────────────

export type ResourceSlug = 'supervisor' | 'mover' | 'driver' | 'truck' | 'truck_driver';

export interface ResourceRate {
  slug: ResourceSlug;
  name: string;
  description: string;
  hourlyRate: number;
}

/** Per-quote crew rate entry — loaded once from org defaults; rates and quantities can be overridden per quote. */
export interface QuoteCrewMember {
  slug: ResourceSlug;
  name: string;
  hourlyRate: number;
  /** Per-resource headcount. Computed by the formula engine; overridable via the breakdown page. */
  quantity?: number;
}

// ── Residential Flat Rates ─────────────────────────────────────────────────

/** One bedroom tier of the org's residential flat-rate card (0 = studio, 5 = "5+"). */
export interface ResidentialFlatRateRoom {
  bedroomCount: number;
  label: string;
  enabled: boolean;
  flatRate: number;
  crewSize?: number;
  trucks?: number;
  jobDurationHours?: number;
}

export interface ResidentialFlatRateAddon {
  slug: string;
  label: string;
  flatRate: number;
}

export interface ResidentialFlatRates {
  rooms: ResidentialFlatRateRoom[];
  addons: ResidentialFlatRateAddon[];
  specialtyItemsEnabled?: boolean;
}

// ── Warehouses ──────────────────────────────────────────────────────────────

export type WarehouseType = 'owned' | 'rented' | 'partner';
export type WarehouseStatus = 'active' | 'inactive';

export interface Warehouse {
  id: string;
  name: string;
  address: string;
  placeId: string;
  note: string;
  type?: WarehouseType;
  status?: WarehouseStatus;
  createdAt: string;
  updatedAt: string;
}

// ── Trucks ──────────────────────────────────────────────────────────────────

export type TruckType = 'cargo_van' | 'sprinter_van' | '12ft_box' | '16ft_box' | '20ft_box' | '24ft_box' | '26ft_box' | 'flatbed' | 'other';
export type TruckStatus = 'active' | 'maintenance' | 'inactive';

/** Default cubic-feet capacity and weight limit for each standard truck type. */
export const TRUCK_CAPACITIES: Record<Exclude<TruckType, 'other'>, { cubicFeet: number; weightLimit: number }> = {
  cargo_van:    { cubicFeet: 250,  weightLimit: 3_000 },
  sprinter_van: { cubicFeet: 400,  weightLimit: 4_000 },
  '12ft_box':   { cubicFeet: 450,  weightLimit: 4_500 },
  '16ft_box':   { cubicFeet: 800,  weightLimit: 6_000 },
  '20ft_box':   { cubicFeet: 1_100, weightLimit: 8_000 },
  '24ft_box':   { cubicFeet: 1_400, weightLimit: 10_000 },
  '26ft_box':   { cubicFeet: 1_700, weightLimit: 10_000 },
  flatbed:      { cubicFeet: 1_000, weightLimit: 15_000 },
};

/** A single truck-type recommendation from the formula engine. */
export interface RecommendedTruckType {
  type: TruckType;
  count: number;
  capacityPerTruck: number;
}

export interface Truck {
  id: string;
  name: string;
  type?: TruckType;
  year?: number;
  make?: string;
  model?: string;
  licensePlate?: string;
  capacity?: number;
  weightLimit?: number;
  /** Warehouse ID, or special values 'on_job' / 'shop'. */
  currentLocation?: string;
  status?: TruckStatus;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

// ── Inventory ───────────────────────────────────────────────────────────────

export interface AddEquipmentEntry {
  itemId: string;
  itemName: string;
  quantity: number;
}

export type QuoteType = 'commercial' | 'residential';

export type ItemizationPreference = 'single_line' | 'crew_trucks' | 'duration_transit' | 'flat_rate_addons';

export type ResidentialItemType = 'customer_item' | 'packing_item' | 'equipment';

export interface MasterInventoryItem {
  id: string;
  name: string;
  type: 'Casegoods' | 'Modular Component' | 'Modular Equipment';
  kz: number;
  description?: string;
  notes?: string;
  imageBase64?: string;
  additionalManHours?: number;
  addEquipment?: AddEquipmentEntry[];
  /** Which quote type this item belongs to. Defaults to 'commercial' when undefined. */
  quoteType?: QuoteType;
  // ── Accessorial service fields (commercial only) ────────────────────────
  /** Flag: this item has an associated accessorial service. Mutually exclusive with additionalManHours. */
  isAccessorial?: boolean;
  /** Label for the accessorial service (e.g., "Glass Bonding"). Required when isAccessorial is true. */
  accessorialLabel?: string;
  /** Man-hours at origin for the accessorial service. */
  accessorialOriginManHours?: number;
  /** Man-hours at destination for the accessorial service. */
  accessorialDestinationManHours?: number;
  // ── Residential-specific fields ─────────────────────────────────────────
  photo?: string;
  moveUnits?: number;
  cubicFeet?: number;
  weightInPounds?: number;
  bulky?: boolean;
  requiredDisassembly?: boolean;
  residentialType?: ResidentialItemType;
  /** Room ID reference — links to a master or org residential room. Residential items only. */
  roomId?: string;
  /** Price per unit in dollars. Packing items and equipment only. */
  unitCost?: number;
  /** Rental length in days. Blank means the item is purchased/consumed rather than returned. */
  rentalDays?: number;
  createdAt: string;
  updatedAt: string;
}

export interface OrgInventoryItem extends MasterInventoryItem {
  /**
   * Set by the list endpoint, which omits `imageBase64`/`photo` to keep the
   * payload small. True when the item has an image to fetch lazily via
   * GET /org-admin/:orgId/inventory/:itemId/image.
   */
  hasImage?: boolean;
  /** 'system' = copied from master; 'org' = created by the org itself */
  source: 'system' | 'org';
  /** ID of the original master item (for system-sourced items) */
  masterItemId?: string;
}

// ── Services ────────────────────────────────────────────────────────────────

export interface MasterServiceItem {
  id: string;
  name: string;
  /** Optional short code / abbreviation for the service (e.g. "A1", "MO2"). */
  code?: string;
  party: 'Customer' | 'Mover';
  note?: string;
  /** Ordering index within the party group. Not displayed in UI. */
  order: number;
  /** Which quote type this service belongs to. Defaults to 'commercial' when undefined. */
  quoteType?: QuoteType;
  createdAt: string;
  updatedAt: string;
}

export interface OrgServiceItem extends MasterServiceItem {
  /** 'system' = copied from master; 'org' = created by the org itself */
  source: 'system' | 'org';
  /** ID of the original master item (for system-sourced items) */
  masterItemId?: string;
}

// ── Residential Rooms ────────────────────────────────────────────────────────

export type RoomProfileLevel = 'minimal' | 'standard' | 'full' | 'heavy';

export interface RoomProfileItem {
  itemId: string;
  itemName: string;
  quantity: number;
}

export interface RoomProfile {
  level: RoomProfileLevel;
  averageSquareFeet?: number;
  items: RoomProfileItem[];
}

export interface MasterResidentialRoom {
  id: string;
  type: 'residential';
  name: string;
  sortOrder?: number;
  profiles: RoomProfile[];
  createdAt: string;
  updatedAt: string;
}

export interface OrgResidentialRoom extends MasterResidentialRoom {
  source: 'system' | 'org';
  masterItemId?: string;
}

// ── Formula ─────────────────────────────────────────────────────────────────

export interface QuoteFormula {
  originOptimumCrewSize: number;
  destinationOptimumCrewSize: number;
  combinedOptimumCrewSize: number;
  totalKZ: number;
  truckloads: number;
  /** Man-hours from KZ-based truckloads for Casegoods items only. */
  casegoodsManHours?: number;
  /** Man-hours from KZ-based truckloads for Modular items before disassembly/reassembly adjustments. */
  modularBaseManHours?: number;
  /** Modular man-hours after disassembly (×0.05) and reassembly (×0.3) adjustments. */
  modularManHours?: number;
  /** Man-hours for modular workstation disassembly: modularBaseManHours × 0.05. */
  modularDisassemblyManHours?: number;
  /** Man-hours for modular workstation reassembly: modularBaseManHours × 0.30. */
  modularReassemblyManHours?: number;
  /** casegoodsManHours + modularManHours + additionalManHours from inventory. Pre-multiplier. */
  baseManHours?: number;
  efficiencyMultiplier: number;
  /** baseManHours × efficiencyMultiplier */
  manHours: number;
  approxCrewSize: number;
  finalCrewSize: number;
  actualJobDuration: number;
  method: 'Shuttle' | 'Load & Go' | 'Internal';
  truckQuantity: number;
  /** Number of drivers. For Load & Go = truckQuantity. For Shuttle, derived from transit time table. */
  driverQuantity?: number;
  travelTimeToJobHours: number;
  finalTransitTimeHours: number;
  totalHours: number;
  totalHourlyRate: number;
  finalCostEstimate: number;
  /** Materials (packing items + equipment) cost for the move's rental length. Included in finalCostEstimate. The estimator can override it; a full recalculation resets it. */
  materialsCost?: number;
  /** Client-listed materials (sum of quantities: packing items + priced equipment) behind materialsCost. The "N" in "Materials (×N)". */
  materialsCount?: number;
  // ── Residential formula fields ─────────────────────────────────────────
  /** Total cubic feet from all residential room items. */
  totalCubicFeet?: number;
  /** Total weight (lbs) from all residential room items. */
  totalWeight?: number;
  /** Total move units from all residential room items. */
  totalMoveUnits?: number;
  /** Extra hours added for bulky items. */
  bulkyItemHours?: number;
  /** Extra hours added for disassembly/reassembly items. */
  disassemblyHours?: number;
  /** Sum of labor points (moveUnits + disassembly penalty + bulky penalty) for crew tier lookup. */
  totalLaborPoints?: number;
  /** Crew size from tier lookup, before access bump. */
  crewSizeTier?: number;
  /** Whether the +1 crew access bump was applied (3+ stories, no elevator). */
  accessBumpApplied?: boolean;
  /** Sum of item load minutes (BASE_MINUTES + disassembly + bulky per item). */
  totalItemMinutes?: number;
  /** totalItemMinutes / crewSize — parallel work. */
  baseLoadMinutes?: number;
  /** Origin flights (stories - 1). */
  originFlights?: number;
  /** Computed origin access multiplier (stairs/elevator + long carry). */
  originAccessMultiplier?: number;
  /** Load minutes at origin (baseLoadMinutes × originMultiplier). */
  loadMinutes?: number;
  /** Destination flights (stories - 1). */
  destFlights?: number;
  /** Computed destination access multiplier (stairs/elevator + long carry). */
  destAccessMultiplier?: number;
  /** Unload minutes at destination (baseLoadMinutes × UNLOAD_FACTOR × destMultiplier). */
  unloadMinutes?: number;
  /** Drive time in minutes (from transit data). */
  driveMinutes?: number;
  /** Flat overhead buffer in minutes. */
  overheadMinutes?: number;
  /** Sum of all time components in minutes. */
  totalMinutes?: number;
  /** Number of bulky items (quantity-weighted). */
  bulkyItemCount?: number;
  /** Number of items requiring disassembly (quantity-weighted). */
  disassemblyItemCount?: number;
  /** Usable truck volume after load efficiency (TRUCK_CAPACITY_CF × LOAD_EFFICIENCY). */
  usableCubicFeet?: number;
  /** Trucks needed by volume: ceil(totalCubicFeet / usableCubicFeet). */
  trucksByVolume?: number;
  /** Trucks needed by weight: ceil(totalWeight / TRUCK_WEIGHT_LIMIT). */
  trucksByWeight?: number;
  /** Recommended truck types and counts based on volume/weight fit. */
  recommendedTruckTypes?: Array<{ type: string; count: number; capacityPerTruck: number }>;
  // ── Commercial extras ──────────────────────────────────────────────────
  /** Extra man-hours for building protection installation when truckloads > 6: max(0, ceil(truckloads) − 6). Included in baseManHours. */
  buildingProtectionManHours?: number;
  // ── Residential flat-rate formula fields ──────────────────────────────────
  /** Whether this formula was computed using the flat-rate path. */
  isFlatRate?: boolean;
  /** Base flat-rate price for the selected room count (before add-ons). */
  flatRateRoomPrice?: number;
  /** Label for the matched room count tier (e.g. "2 Bedrooms"). */
  flatRateRoomLabel?: string;
  /** Bedroom count that matched the flat-rate tier. */
  flatRateBedroomCount?: number;
  /** Add-on line items applied to the flat-rate quote. */
  flatRateAddons?: Array<{ slug: string; label: string; amount: number }>;
  /** Total flat-rate price (room price + sum of add-ons). */
  flatRateTotal?: number;
  calculatedAt: string;
  /**
   * Fingerprint of the formula inputs at the time this formula was computed.
   * Used by useFormulaEngine to skip re-computation when inputs haven't changed,
   * so manual adjustments on the breakdown page survive navigation.
   */
  formulaInputsFingerprint?: string;
  // ── Tax output fields ───────────────────────────────────────────────────
  /** 2-letter state code that governs the tax rate. */
  taxState?: string;
  /** Tax rate applied to labor/moving services (e.g. 0.085 for 8.5%). 0 if not taxable. */
  laborTaxRate?: number;
  /** Tax rate applied to materials; 0 when the governing state does not tax them. */
  materialsTaxRate?: number;
  /** Calculated tax amount: subtotal × laborTaxRate. */
  taxAmount?: number;
  /** Total cost including tax: finalCostEstimate + taxAmount. */
  totalWithTax?: number;
  /** True when interstate move + governingStateRule = 'manual_review'. */
  requiresManualTaxReview?: boolean;
  /** True when the org's defaultTaxRate was used instead of a zip.tax lookup. Backend/audit only. */
  usedDefaultTaxRate?: boolean;
}

// ── Tax ─────────────────────────────────────────────────────────────────────

/** Tax data attached to quotes, versions, and payment summaries. */
export interface TaxInfo {
  /** 2-letter state code that governs the tax rate (always populated, even if no tax). */
  taxState: string;
  /** Tax rate for labor/moving services (0 if not taxable). */
  laborTaxRate: number;
  /** Calculated tax: subtotal × laborTaxRate. */
  taxAmount: number;
  /** Pre-tax total. */
  subtotal: number;
  /** subtotal + taxAmount. */
  totalWithTax: number;
  /** True when interstate + governingStateRule = 'manual_review'. */
  requiresManualTaxReview?: boolean;
  /** True when the org's defaultTaxRate was used instead of a zip.tax lookup. Backend/audit only. */
  usedDefaultTaxRate?: boolean;
}

// ── Quote ───────────────────────────────────────────────────────────────────

export type LifecycleStage = 'lead' | 'quote' | 'job';

export const LIFECYCLE_STAGE_LABEL: Record<LifecycleStage, string> = {
  lead: 'Lead',
  quote: 'Quote',
  job: 'Job',
};

export type LeadStatus = 'new' | 'contacted' | 'estimate_scheduled' | 'not_pursuing' | 'lost';

export const LEAD_STATUS_LABEL: Record<LeadStatus, string> = {
  new: 'New',
  contacted: 'Contacted',
  estimate_scheduled: 'Estimate Scheduled',
  not_pursuing: 'Not Pursuing',
  lost: 'Lost',
};

export type LeadSource =
  | 'google'
  | 'yelp'
  | 'homeadvisor_angi'
  | 'thumbtack'
  | 'nextdoor'
  | 'website_form'
  | 'repeat_customer'
  | 'referral'
  | 'social_media'
  | 'trade_show_event'
  | 'phone_call_inbound'
  | 'partner_broker'
  | 'other';

export const LEAD_SOURCE_LABEL: Record<LeadSource, string> = {
  google: 'Google',
  yelp: 'Yelp',
  homeadvisor_angi: 'HomeAdvisor / Angi',
  thumbtack: 'Thumbtack',
  nextdoor: 'Nextdoor',
  website_form: 'Website Form',
  repeat_customer: 'Repeat Customer',
  referral: 'Referral',
  social_media: 'Social Media',
  trade_show_event: 'Trade Show / Event',
  phone_call_inbound: 'Phone Call (Inbound)',
  partner_broker: 'Partner / Broker',
  other: 'Other',
};

// ── Inbound Channel + Discovery Source (replaces LeadSource for new leads) ──

/** How the lead entered the system — internal use, not customer-facing. */
export type InboundChannel =
  | 'booking_form'
  | 'website_form'
  | 'phone_call_inbound'
  | 'walk_in'
  | 'partner_broker'
  | 'manual_entry';

export const INBOUND_CHANNEL_LABEL: Record<InboundChannel, string> = {
  booking_form: 'Online Booking Form',
  website_form: 'Website Form',
  phone_call_inbound: 'Phone Call (Inbound)',
  walk_in: 'Walk-in',
  partner_broker: 'Partner / Broker',
  manual_entry: 'Manual Entry',
};

/** How the customer heard about the company — customer-facing. */
export type DiscoverySource =
  | 'google'
  | 'yelp'
  | 'homeadvisor_angi'
  | 'thumbtack'
  | 'nextdoor'
  | 'social_media'
  | 'repeat_customer'
  | 'referral'
  | 'trade_show_event'
  | 'other';

export const DISCOVERY_SOURCE_LABEL: Record<DiscoverySource, string> = {
  google: 'Google',
  yelp: 'Yelp',
  homeadvisor_angi: 'HomeAdvisor / Angi',
  thumbtack: 'Thumbtack',
  nextdoor: 'Nextdoor',
  social_media: 'Social Media',
  repeat_customer: 'Repeat Customer',
  referral: 'Referral',
  trade_show_event: 'Trade Show / Event',
  other: 'Other',
};

export type QuoteStatus = 'draft' | 'sent' | 'response_received' | 'booked' | 'declined' | 'cancelled';

export type JobStatus = 'scheduled' | 'in_progress' | 'completed' | 'on_hold' | 'cancelled';

export const JOB_STATUS_LABEL: Record<JobStatus, string> = {
  scheduled: 'Scheduled',
  in_progress: 'In Progress',
  completed: 'Completed',
  on_hold: 'On Hold',
  cancelled: 'Cancelled',
};

export const JOB_STATUS_CLASSES: Record<JobStatus, string> = {
  scheduled: 'bg-blue-50 text-blue-700',
  in_progress: 'bg-amber-50 text-amber-700',
  completed: 'bg-emerald-50 text-emerald-700',
  on_hold: 'bg-stone text-slate',
  cancelled: 'bg-rose/10 text-rose',
};

export const QUOTE_STATUS_LABEL: Record<QuoteStatus, string> = {
  draft: 'Draft',
  sent: 'Sent',
  response_received: 'Response Received',
  booked: 'Booked',
  declined: 'Declined',
  cancelled: 'Cancelled',
};

export type MoveTiming = 'Work week - day' | 'Work week - evening' | 'Weekend';

export const SHIFT_LENGTH_HOURS: Record<MoveTiming, number> = {
  'Work week - day': 7,
  'Work week - evening': 5,
  'Weekend': 8,
};

/** An org-configurable shift type with a custom name and length in hours. */
export interface ShiftSetting {
  name: string;
  lengthHours: number;
  /**
   * Whether this shift is the default selection for new quotes. Exactly one
   * shift in the list should be marked default at any time; the backend
   * normalizes the list on write to enforce this.
   */
  isDefault?: boolean;
}

/** Default shift settings seeded for new organizations. */
export const DEFAULT_SHIFT_SETTINGS: ShiftSetting[] = [
  { name: 'Work week - day', lengthHours: 7, isDefault: true },
  { name: 'Work week - evening', lengthHours: 5 },
  { name: 'Weekend', lengthHours: 8 },
];

export type WaypointType = 'Truck' | 'Stairs' | 'Elevator';

export interface LogisticsData {
  /** Strides from start-node to waypoint (or directly to end-node if waypoint = Truck) */
  strides1?: number;
  distanceFeet1?: number;
  /** The intermediate waypoint. 'Truck' = no intermediate stop (walk directly to end). */
  waypoint?: WaypointType;
  /** Number of floor stories traversed via stairs. Only relevant when waypoint = 'Stairs'. */
  stories?: number;
  /** Strides from waypoint to end-node. Only relevant when waypoint != 'Truck'. */
  strides2?: number;
  distanceFeet2?: number;
  /** Whether a truck-height loading dock is available at this location. */
  loadingDock?: boolean;
}

export interface ModularWorkstationSample {
  itemId: string;
  itemName: string;
  quantitySampled: number;
}

export interface ModularWorkstations {
  totalWorkstations: number;
  moverDisassembling: boolean;
  moverReassembling: boolean;
  workstationsSampled: number;
  samples: ModularWorkstationSample[];
}

export interface QuoteServiceItem {
  serviceId: string;
  serviceName: string;
  /** Optional short code / abbreviation for the service (e.g. "A1", "MO2"). */
  code?: string;
  party: 'Customer' | 'Mover';
  order: number;
  enabled: boolean;
}

export type ResidentialPropertyType =
  | 'single_family'
  | 'condo'
  | 'townhouse'
  | 'apartment'
  | 'manufactured'
  | 'multi_family';

export interface ResidentialPropertyDetails {
  propertyType?: ResidentialPropertyType;
  bedrooms?: number;
  bathrooms?: number;
  basement?: boolean;
  squareFootage?: number;
  stories?: number;
  elevator?: boolean;
  garageSpaces?: number;
  totalRoomCount?: number;
  pool?: boolean;
  /** Truck cannot park close to the building entrance (long driveway, distant loading dock, etc.). */
  longCarry?: boolean;
}

export type SmartProfileLevel = 'minimal' | 'standard' | 'heavy' | 'full';

export interface SmartProfileThreshold {
  level: SmartProfileLevel;
  maxSqft?: number; // undefined for 'full' (catch-all)
}

export interface SmartRoomProfileConfig {
  propertyType: ResidentialPropertyType;
  thresholds: SmartProfileThreshold[];
  updatedAt?: string;
}

export interface QuoteAddress {
  address: string;
  placeId: string;
  floor?: string;
  officeNumber?: string;
  apt?: string;
  residentialProperty?: ResidentialPropertyDetails;
}

export interface QuoteTransit {
  distanceMeters: number;
  durationSeconds: number;
  calculatedAt: string;
}

export interface CasegoodsInventoryItem {
  itemId: string;
  itemName: string;
  quantity: number;
}

/** A material's price as locked into a quote. `null` = the item had no unit cost / rental length. */
export interface LockedMaterialPrice {
  unitCost: number | null;
  rentalDays: number | null;
}

// ── Quote Residential Rooms ─────────────────────────────────────────────────

export interface QuoteResidentialRoomItem {
  itemId: string;
  itemName: string;
  quantity: number;
  cubicFeet?: number;
  weightInPounds?: number;
  moveUnits?: number;
  bulky?: boolean;
  requiredDisassembly?: boolean;
}

export type QuoteRoomProfileLevel = 'empty' | RoomProfileLevel;

export interface QuoteResidentialRoom {
  id: string;
  roomId: string;
  roomName: string;
  profileLevel: QuoteRoomProfileLevel;
  items: QuoteResidentialRoomItem[];
  notes?: string;
}

export interface WarehouseTransitLeg {
  distanceMeters: number;
  durationSeconds: number;
  calculatedAt: string;
}

export interface TravelToJob {
  originWarehouseId?: string;
  destinationWarehouseId?: string;
  originTravelSeconds?: number;
  destinationTravelSeconds?: number;
}

export interface QuotePhoto {
  id: string;
  fileName: string;
  url: string;
  createdAt: string;
}

// ── Job Issues ───────────────────────────────────────────────────────────

export type JobIssueCategory =
  | 'access_site'
  | 'item'
  | 'scope_client'
  | 'crew_equipment'
  | 'delay'
  | 'other';

export type JobIssueSeverity = 'low' | 'medium' | 'high';
export type JobIssueResponsibility = 'mover' | 'client' | 'building' | 'external';

export const JOB_ISSUE_CATEGORIES: Record<JobIssueCategory, string> = {
  access_site: 'Access & Site',
  item: 'Item',
  scope_client: 'Scope & Client',
  crew_equipment: 'Crew & Equipment',
  delay: 'Delay',
  other: 'Other',
};

export const JOB_ISSUE_OPTIONS: Record<JobIssueCategory, string[]> = {
  access_site: ['Elevator issue', 'Loading dock issue', 'Parking issue', 'Building access issue', 'Other'],
  item: ['Pre-existing damage', 'Damaged during move', 'Missing item', 'Client added / removed item', 'Other'],
  scope_client: ['Client not present', 'Client requested change', 'Client refused sign-off', 'Other'],
  crew_equipment: ['Crew issue', 'Truck / equipment issue', 'Other'],
  delay: ['Unexpected delay'],
  other: ['Other'],
};

export const JOB_ISSUE_SEVERITY_LABEL: Record<JobIssueSeverity, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
};

export const JOB_ISSUE_RESPONSIBILITY_LABEL: Record<JobIssueResponsibility, string> = {
  mover: 'Mover',
  client: 'Client',
  building: 'Building',
  external: 'External',
};

export interface JobIssue {
  id: string;
  category: JobIssueCategory;
  issue: string;
  severity: JobIssueSeverity;
  responsibility: JobIssueResponsibility;
  notes: string;
  photos?: QuotePhoto[];
  loggedBy: {
    userId: string;
    displayName: string;
  };
  timestamp: string;
}

// ── Crew & Truck Assignment ──────────────────────────────────────────────

/** A crew member assigned to a job. */
export interface AssignedCrewMember {
  /** User ID — omitted for contractors (not app users). */
  userId?: string;
  /** Display name of the crew member or contractor. */
  displayName: string;
  /** Whether this person is the Crew Lead (replaces the old Move Lead concept). Only one per job. */
  isCrewLead: boolean;
  /** Whether this person is a driver. */
  isDriver: boolean;
  /** Whether this is a contractor (not an app user). */
  isContractor: boolean;
}

/** A truck assigned to a job. */
export interface AssignedTruck {
  truckId: string;
  truckName: string;
}

export interface Quote {
  id: string;
  orgId: string;
  /** Human-readable quote identifier, e.g. "QT-00001". Unique per org. */
  quoteNumber?: string;
  /** Quote type — 'commercial' (default) or 'residential'. */
  type?: QuoteType;
  customerCompanyName: string;
  quoteStatus: QuoteStatus;
  createdBy: string;
  createdByName?: string;
  /** UID of the current quote lead — resolved server-side from the quoteLeadUser reference. */
  quoteLeadUserId?: string;
  /** Display name of the current quote lead — resolved live from users/{uid}. */
  quoteLeadDisplayName?: string;
  createdAt: string;
  updatedAt: string;
  numberOfEmployees?: number;
  pointOfContact?: {
    name?: string;
    phone?: string;
    email?: string;
    notes?: string;
  };
  movingDate?: {
    /** Preferred move date — `YYYY-MM-DD` or `YYYY-MM-DDTHH:mm` (time optional). */
    preferredDate?: string;
    /** Move deadline — `YYYY-MM-DD` or `YYYY-MM-DDTHH:mm` (time optional). */
    deadline?: string;
    timing?: MoveTiming;
    /** Snapshot of the selected shift type name (persisted so edits/deletes to org shifts don't affect past quotes). */
    shiftType?: string;
    /** Shift length in hours corresponding to the selected shift type. */
    shiftLengthHours?: number;
  };
  addresses?: {
    origin?: QuoteAddress;
    internalMove?: boolean;
    destination?: QuoteAddress;
    transit?: QuoteTransit;
  };
  casegoodsInventory?: CasegoodsInventoryItem[];
  residentialRooms?: QuoteResidentialRoom[];
  /** How many days this move rents its materials for (whole days, default 1). Items with a rental length are billed in whole periods against this. */
  materialsRentalDays?: number;
  /**
   * Material prices locked into this quote, by inventory item id. Like `crew`,
   * each item's unit cost and rental length are copied from the org's catalog
   * the first time the item is on the quote, and later catalog edits don't
   * change them. Items not in the map are priced from the catalog.
   */
  materialPrices?: Record<string, LockedMaterialPrice>;
  modularWorkstations?: ModularWorkstations;
  scopeOfServices?: QuoteServiceItem[];
  originLogistics?: LogisticsData;
  destinationLogistics?: LogisticsData;
  formula?: QuoteFormula;
  /** Crew resource rates for this quote — loaded once from org defaults; rates can be overridden per quote. */
  crew?: QuoteCrewMember[];
  /** Transit times from each warehouse (by warehouseId) to origin and destination job sites. */
  warehouseTransit?: Record<string, { toOrigin?: WarehouseTransitLeg; toDestination?: WarehouseTransitLeg }>;
  /** Selected warehouse IDs and their recorded travel times for this quote. */
  travelToJob?: TravelToJob;
  /** Travel time rate method for this quote — defaults to org setting on first save. */
  travelTimeRate?: TravelTimeRate;
  /** Efficiency multiplier applied to man-hours — frozen from org setting on first formula run. */
  efficiencyMultiplier?: number;
  /** Free-form estimator notes. */
  notes?: string;
  /** Photo metadata — actual files stored in Firebase Storage. */
  photos?: QuotePhoto[];
  /** @deprecated Use assignedCrew with isCrewLead instead. UID of the assigned Move Lead. */
  moveLeadUserId?: string;
  /** @deprecated Use assignedCrew with isCrewLead instead. Display name of the Move Lead. */
  moveLeadDisplayName?: string;
  /** Crew members assigned to this job. Replaces moveLeadUserId. */
  assignedCrew?: AssignedCrewMember[];
  /** Trucks assigned to this job. */
  assignedTrucks?: AssignedTruck[];
  /** Job status — independent of quote status. Set when a Move Lead is assigned. */
  jobStatus?: JobStatus;
  /** Actual move completion date — `YYYY-MM-DD` or `YYYY-MM-DDTHH:mm` (time optional). Set when job is marked completed. */
  moveCompletionDate?: string;
  /** Win/loss outcome — recorded when quoteStatus changes to booked or declined. */
  outcome?: QuoteOutcome;
  /** Denormalized latest client response from any quote version — used for list queries. */
  latestClientResponse?: LatestClientResponse;
  /** External CRM record ID (e.g., SmartMoving job ID). Unique per org. */
  externalId?: string;
  /** Which system created the quote (e.g., "smartmoving", "supermove", "manual"). */
  source?: string;
  /** Packing date — `YYYY-MM-DD` or `YYYY-MM-DDTHH:mm` (time optional). */
  packingDate?: string | null;
  /** Accessorial services date — `YYYY-MM-DD` or `YYYY-MM-DDTHH:mm` (time optional). Must be ≤ move date if set. */
  accessorialServicesDate?: string | null;
  /** Move type — auto-populated from addresses, overridable by user. */
  moveType?: 'local' | 'intrastate' | 'interstate';
  /** Whether this is a binding quote (guarantees price) or non-binding (estimate). */
  bindingQuote?: boolean;
  /** Lifecycle stage — distinguishes leads, quotes, and jobs within the same data model. */
  lifecycleStage?: LifecycleStage;
  /** Lead status — only used when lifecycleStage === 'lead'. */
  leadStatus?: LeadStatus;
  /** Reason the lead was lost — only set when leadStatus === 'lost'. */
  leadLostReason?: string;
  /** Reason the lead is not being pursued — only set when leadStatus === 'not_pursuing'. */
  leadNotPursuingReason?: string;
  /** Optional free-text note for not-pursuing reason. */
  leadNotPursuingNote?: string;
  /** @deprecated Use inboundChannel + discoverySource instead. How the lead was acquired. */
  leadSource?: LeadSource;
  /** How the lead entered the system (e.g. booking form, manual entry, phone call). */
  inboundChannel?: InboundChannel;
  /** How the customer heard about the company (e.g. Google, referral, Yelp). Customer-facing. */
  discoverySource?: DiscoverySource;
  /** Who referred this lead — only relevant when discoverySource === 'referral' (or legacy leadSource === 'referral'). */
  referredBy?: string;
  /** Date when someone should follow up with the lead. ISO date string. */
  nextFollowUp?: string;
  /** Rough dollar estimate of the move value — helps prioritize leads. */
  estimatedValue?: number;
  /** Free text for vague move dates like "June" or "last week of October". */
  estimatedMoveDateRange?: string;
  /** Origin zip code — fallback when full address not available. */
  originZipCode?: string;
  /** Destination zip code — fallback when full address not available. */
  destinationZipCode?: string;
  /** Number of bedrooms — residential leads/quotes only. */
  numberOfBedrooms?: number;
  /** Default crew size from residential flat rate room config. */
  flatRateCrewSize?: number;
  /** Default truck quantity from residential flat rate room config. */
  flatRateTrucks?: number;
  /** Default job duration (hours) from residential flat rate room config. */
  flatRateJobDurationHours?: number;
  /** Per-quote pricing model override. When set to 'hourly_rate', a flat-rate residential
   *  quote behaves as hourly-rate for this quote only (Rooms & Inventory becomes active). */
  pricingOverride?: 'hourly_rate';
  /** Square footage — residential leads/quotes only. */
  squareFootage?: number;
  /** Number of floors at origin. */
  originStories?: number;
  /** Number of floors at destination. */
  destinationStories?: number;
  /** Whether elevator access is available at origin. */
  originElevatorAccess?: boolean;
  /** Whether elevator access is available at destination. */
  destinationElevatorAccess?: boolean;
  /** Unique token for the public booking form to identify this lead (UUID). */
  bookingToken?: string;
  /** Customer's chosen action from the booking form. */
  bookingAction?: 'lock_in_date' | 'request_estimate';
  /** Whether the dates are flexible (from booking form). */
  flexibleDate?: boolean;
  /** Payment schedule for this quote/job — defines installments and their statuses. */
  paymentSchedule?: PaymentSchedule;
  /** Aggregated payment summary — denormalized for quick access on list/detail views. */
  paymentSummary?: JobPaymentSummary;
  /** Billing information for quotes and invoices. */
  billingInfo?: {
    sameAsPointOfContact?: boolean;
    name?: string;
    sameAsOriginAddress?: boolean;
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    state?: string;
    zipCode?: string;
  };
  // ── Tax fields (populated when formula runs and org has calculateTaxes enabled) ──
  /** 2-letter state code that governs the tax rate. */
  taxState?: string;
  /** Tax rate applied to labor/moving services (e.g. 0.085 for 8.5%). */
  laborTaxRate?: number;
  /** Tax rate applied to materials; 0 when the governing state does not tax them. */
  materialsTaxRate?: number;
  /** Calculated tax amount. */
  taxAmount?: number;
  /** True when interstate move + governingStateRule = 'manual_review'. */
  requiresManualTaxReview?: boolean;
  /** True when the org's defaultTaxRate was used instead of a zip.tax lookup. Backend/audit only. */
  usedDefaultTaxRate?: boolean;
}

export interface QuoteOutcome {
  reason: string;
  note?: string;
  recordedAt: string;
  recordedBy: string;
}

export type QuotePatch = Partial<Pick<Quote,
  | 'customerCompanyName'
  | 'numberOfEmployees'
  | 'pointOfContact'
  | 'movingDate'
  | 'addresses'
  | 'casegoodsInventory'
  | 'residentialRooms'
  | 'modularWorkstations'
  | 'scopeOfServices'
  | 'originLogistics'
  | 'destinationLogistics'
  | 'formula'
  | 'crew'
  | 'travelToJob'
  | 'travelTimeRate'
  | 'efficiencyMultiplier'
  | 'quoteStatus'
  | 'createdBy'
  | 'createdByName'
  | 'quoteLeadUserId'
  | 'notes'
  | 'moveLeadUserId'
  | 'assignedCrew'
  | 'assignedTrucks'
  | 'jobStatus'
  | 'moveCompletionDate'
  | 'outcome'
  | 'externalId'
  | 'source'
  | 'packingDate'
  | 'accessorialServicesDate'
  | 'moveType'
  | 'bindingQuote'
  | 'lifecycleStage'
  | 'leadStatus'
  | 'leadLostReason'
  | 'leadNotPursuingReason'
  | 'leadNotPursuingNote'
  | 'leadSource'
  | 'inboundChannel'
  | 'discoverySource'
  | 'referredBy'
  | 'nextFollowUp'
  | 'estimatedValue'
  | 'estimatedMoveDateRange'
  | 'originZipCode'
  | 'destinationZipCode'
  | 'numberOfBedrooms'
  | 'pricingOverride'
  | 'squareFootage'
  | 'originStories'
  | 'destinationStories'
  | 'originElevatorAccess'
  | 'destinationElevatorAccess'
  | 'bookingToken'
  | 'bookingAction'
  | 'flexibleDate'
  | 'billingInfo'
>>;

// ── SmartMoving Integration ──────────────────────────────────────────────

export interface SmartMovingConfig {
  enabled: boolean;
  apiKey: string;
  providerKey?: string;
  baseUrl?: string;
  syncEnabled: boolean;
  syncIntervalMinutes: number;
  lastSyncAt: string | null;
  lastSyncStatus: 'success' | 'error' | null;
  lastSyncError: string | null;
  syncFilters: {
    serviceTypes: string[];
    statuses: string[];
  };
  fieldMapping: Record<string, string>;
  pushBackEnabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SyncLogEntry {
  id: string;
  timestamp: string;
  direction: 'inbound' | 'outbound';
  action: 'import' | 'update' | 'push';
  smartMovingId: string;
  iomiQuoteId?: string;
  status: 'success' | 'error' | 'skipped';
  details: string;
  error?: string;
}

// ── App Versions & Release Notes ──────────────────────────────────────────

export type AppPlatform = 'web' | 'ios' | 'android';

export interface PlatformVersion {
  platform: AppPlatform;
  currentVersion: string;
  minimumVersion: string;
  storeUrl?: string;
  updatedAt: string;
  updatedBy: string;
}

export interface ReleaseNote {
  id: string;
  version: string;
  platforms: AppPlatform[];
  title: string;
  body: string;
  published: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

export interface VersionCheckResult {
  updateAvailable: boolean;
  forceUpdate: boolean;
  latestVersion: string;
  minimumVersion: string;
  storeUrl: string | null;
}

// ── Demo Quote Template ───────────────────────────────────────────────────

/** Admin-editable template used by the onboarding wizard to create demo quotes. */
export interface DemoQuoteTemplate {
  customerCompanyName?: string;
  numberOfEmployees?: number;
  pointOfContact?: {
    name?: string;
    phone?: string;
    email?: string;
    notes?: string;
  };
  /** Display-only lead names — not linked to real user accounts. */
  quoteLeadDisplayName?: string;
  moveLeadDisplayName?: string;
  movingDate?: {
    timing?: MoveTiming;
  };
  addresses?: {
    origin?: QuoteAddress;
    internalMove?: boolean;
    destination?: QuoteAddress;
  };
  originLogistics?: LogisticsData;
  destinationLogistics?: LogisticsData;
  /** References master inventory item IDs (not org-specific). */
  casegoodsInventory?: CasegoodsInventoryItem[];
  modularWorkstations?: ModularWorkstations;
  /** References master service IDs (not org-specific). */
  scopeOfServices?: QuoteServiceItem[];
  notes?: string;
  updatedAt?: string;
  updatedBy?: string;
}

// ── Client Response ────────────────────────────────────────────────────────

export type ClientResponseType = 'accepted' | 'changes_requested' | 'declined';

export const CLIENT_RESPONSE_LABEL: Record<ClientResponseType, string> = {
  accepted: 'Client Accepted',
  changes_requested: 'Changes Requested',
  declined: 'Client Declined',
};

export interface ClientResponse {
  type: ClientResponseType;
  message?: string;
  respondedAt: string;
}

export interface LatestClientResponse {
  type: ClientResponseType;
  versionId: string;
  respondedAt: string;
}

// ── Quote Versions ──────────────────────────────────────────────────────────

export interface QuoteVersionLineItem {
  name: string;
  rate: number;
  qty: number;
  total: number;
  /** 'materials' lines are a flat amount for N items, not rate × hours. */
  kind?: 'materials';
}

// ── Notifications ──────────────────────────────────────────────────────────

export type NotificationType =
  | 'quote_created'
  | 'quote_lead_assigned'
  | 'quote_sent'
  | 'quote_response_received'
  | 'quote_booked'
  | 'quote_declined'
  | 'move_lead_assigned'
  | 'job_status_changed'
  | 'lead_follow_up_reminder'
  | 'payment_succeeded'
  | 'payment_failed'
  | 'connect_account_updated';

export type NotificationPriority = 'urgent' | 'high' | 'normal';

export interface AppNotification {
  id: string;
  recipientUid: string;
  type: NotificationType;
  title: string;
  message: string;
  resourceType: 'quote' | 'job' | 'lead';
  resourceId: string;
  orgId: string;
  priority: NotificationPriority;
  read: boolean;
  createdAt: string;
  readAt?: string;
}

/** Per-org email notification preferences stored on the org user document. */
export type NotificationPreferences = Record<NotificationType, boolean>;

/** Human-readable labels for each notification type. */
export const NOTIFICATION_TYPE_LABELS: Record<NotificationType, string> = {
  quote_created: 'Quote Created',
  quote_lead_assigned: 'Quote Lead Assigned',
  quote_sent: 'Quote Sent',
  quote_response_received: 'Response Received',
  quote_booked: 'Quote Booked',
  quote_declined: 'Quote Declined',
  move_lead_assigned: 'Move Lead Assigned',
  job_status_changed: 'Job Status Changed',
  lead_follow_up_reminder: 'Lead Follow-Up Reminder',
  payment_succeeded: 'Payment Succeeded',
  payment_failed: 'Payment Failed',
  connect_account_updated: 'Connect Account Updated',
};

/** All notification types — convenience array for iterating. */
export const ALL_NOTIFICATION_TYPES: NotificationType[] = [
  'quote_created',
  'quote_lead_assigned',
  'quote_sent',
  'quote_response_received',
  'quote_booked',
  'quote_declined',
  'move_lead_assigned',
  'job_status_changed',
  'lead_follow_up_reminder',
  'payment_succeeded',
  'payment_failed',
  'connect_account_updated',
];

// ── Quote Versions ──────────────────────────────────────────────────────────

// ── Quote History Log ──────────────────────────────────────────────────────

export type QuoteHistoryType =
  | 'created'
  | 'status_change'
  | 'lead_changed'
  | 'move_lead_changed'
  | 'version_created'
  | 'section_updated'
  | 'formula_calculated'
  | 'date_changed'
  | 'address_changed'
  | 'photos_added'
  | 'customer_replied'
  | 'job_status_changed';

/** @deprecated Use QuoteHistoryType */
export type QuoteActivityType = QuoteHistoryType;

export interface QuoteHistoryEntry {
  id: string;
  type: QuoteHistoryType;
  description: string;
  userId: string;
  userDisplayName: string;
  timestamp: string;
  metadata?: Record<string, unknown>;
}

/** @deprecated Use QuoteHistoryEntry */
export type QuoteActivityEntry = QuoteHistoryEntry;

/** Snapshot of a packing-item material included in a quote version. */
export interface QuoteVersionMaterial {
  itemName: string;
  quantity: number;
}

/** Snapshot of an enabled scope-of-service included in a quote version. */
export interface QuoteVersionService {
  serviceName: string;
  /** Optional short code / abbreviation for the service (e.g. "A1", "MO2"). */
  code?: string;
  party: 'Customer' | 'Mover';
}

export interface QuoteVersion {
  id: string;
  quoteId: string;
  versionNumber: number;
  /** Full version ID: e.g. "QT-00001-1" */
  quoteVersionId: string;
  /** Quote creation date — `YYYY-MM-DD`. */
  quoteDate: string;
  customerCompanyName: string;
  originAddress?: string;
  destinationAddress?: string;
  pointOfContactEmail?: string;
  /** Preferred move date — `YYYY-MM-DD` or `YYYY-MM-DDTHH:mm` (time optional). */
  preferredMoveDate?: string;
  /** Packing date — `YYYY-MM-DD` or `YYYY-MM-DDTHH:mm` (time optional). */
  packingDate?: string;
  /** Move deadline — `YYYY-MM-DD` or `YYYY-MM-DDTHH:mm` (time optional). */
  moveDeadline?: string;
  bindingQuote?: boolean;
  lineItems: QuoteVersionLineItem[];
  totalQuotedCost: number;
  /** Itemization preference used when this version was generated. */
  itemizationPreference?: ItemizationPreference;
  /** Snapshot of packing-item materials (only present if itemize-materials is enabled for the quote type). */
  materials?: QuoteVersionMaterial[];
  /** Snapshot of enabled scope-of-service items (only present if itemize-scope is enabled for the quote type). */
  scopeOfServices?: QuoteVersionService[];
  pdfUrl?: string;
  /** ISO timestamp when this version expires. Absent = never expires. */
  expiresAt?: string;
  /** The validity period (in days) used when this version was created. */
  validityDays?: number | null;
  /** Point of contact name snapshot. */
  pointOfContactName?: string;
  /** Billing info snapshot. */
  billingName?: string;
  billingAddressLine1?: string;
  billingAddressLine2?: string;
  billingCity?: string;
  billingState?: string;
  billingZipCode?: string;
  createdBy: string;
  createdAt: string;
  /** Unique access token for the client portal. */
  portalToken?: string;
  /** Full URL to the client-facing online quote (built by the backend). */
  portalUrl?: string;
  /** ISO timestamp when the client first viewed this version via the portal. */
  clientViewedAt?: string;
  /** Total number of portal views. */
  clientViewCount?: number;
  /** Client's response to this quote version. */
  clientResponse?: ClientResponse;
  // ── Tax fields ──
  /** 2-letter state code that governs the tax rate. */
  taxState?: string;
  /** Tax rate applied to labor/moving services. */
  laborTaxRate?: number;
  /** Tax rate applied to materials; 0 when the governing state does not tax them. */
  materialsTaxRate?: number;
  /** Calculated tax amount. */
  taxAmount?: number;
  /** Pre-tax total. */
  subtotal?: number;
  /** Total including tax: subtotal + taxAmount. */
  totalWithTax?: number;
  /** True when interstate + governingStateRule = 'manual_review'. */
  requiresManualTaxReview?: boolean;
  /** True when the org's defaultTaxRate was used instead of a zip.tax lookup. Backend/audit only. */
  usedDefaultTaxRate?: boolean;
}

// ── Plan Feature Mapping ──────────────────────────────────────────────────────

/** Keys used in PlanFeatureMap.allowedFeatures — each corresponds to an allowedFeatureX org field. */
export type PlanFeatureKey =
  | 'dispatch'
  | 'jobs'
  | 'clientPortal'
  | 'quotePdfs'
  | 'smartMovingIntegration'
  | 'commercialMovingQuotes'
  | 'residentialMovingQuotes'
  | 'leadPipeline'
  | 'fleetManagement'
  | 'onlineLeadCapture'
  | 'stripeConnect'
  | 'customerPayments';

/** Maps a Stripe product (plan) to the feature flags it grants. */
export interface PlanFeatureMap {
  /** Stripe product ID (prod_...). Also used as the Firestore document ID. */
  productId: string;
  /** Display name of the plan (cached from Stripe for convenience). */
  productName: string;
  /** Which features this plan allows. Missing keys are treated as false (not allowed). */
  allowedFeatures: Partial<Record<PlanFeatureKey, boolean>>;
  /** Maximum number of users allowed on this plan. null or undefined = no limit. */
  maxUsers?: number | null;
  updatedAt: string;
  updatedBy: string;
}
